/* Change one sleeve while retaining its bundle properties and selling plan. */
class OzeraCartVariantChange {
  constructor(request) { this.request = request; }

  identity(item, variantId = item.variant_id) {
    return JSON.stringify([Number(variantId), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || null]);
  }

  matching(cart, identity) { return cart.items.filter(item => this.identity(item) === identity); }
  count(cart, identity) { return this.matching(cart, identity).reduce((sum, item) => sum + item.quantity, 0); }

  async readCart() {
    try { return await this.request('cart.js'); }
    catch { return this.request('cart.js'); }
  }

  async replace(key, variantId, quantity = 1) {
    const before = await this.readCart();
    const source = before.items.find(item => item.key === key);
    if (!source) throw new Error('Košík se mezitím změnil. Vyberte prosím velikost znovu.');
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > source.quantity) throw new Error('Košík se mezitím změnil. Vyberte prosím velikost znovu.');
    if (Number(source.variant_id) === Number(variantId)) return;
    const oldIdentity = this.identity(source);
    const newIdentity = this.identity(source, variantId);
    const oldCount = this.count(before, oldIdentity);
    const newCount = this.count(before, newIdentity);
    const added = { id: Number(variantId), quantity, properties: source.properties || {} };
    if (source.selling_plan_allocation) added.selling_plan = source.selling_plan_allocation.selling_plan.id;
    let failure;
    try { await this.request('cart/add.js', { items: [added] }); }
    catch (error) { failure = error; }
    // Even a failed response can follow a successful write, or a partial stock fill.
    let current = await this.readCart();
    try {
      if (this.count(current, newIdentity) !== newCount + quantity) {
        throw failure || new Error('Vybraná velikost není dostupná v požadovaném počtu.');
      }
      // A free and a paid sleeve can merge as the bundle discount moves to the
      // new size. Match bundle properties, then subtract just the selected unit
      // from the current line quantity, regardless of its previous key/price.
      if (this.count(current, oldIdentity) !== oldCount) throw new Error('Košík se mezitím změnil. Zkuste to prosím znovu.');
      const oldLines = this.matching(current, oldIdentity);
      const oldLine = oldLines.find(item => item.key === key && item.quantity >= quantity) || oldLines.find(item => item.quantity >= quantity);
      if (!oldLine) throw new Error('Košík se mezitím změnil. Zkuste to prosím znovu.');
      try { await this.request('cart/change.js', { id: oldLine.key, quantity: oldLine.quantity - quantity }); }
      catch (error) { failure = error; }
      current = await this.readCart();
      if (this.count(current, oldIdentity) === oldCount - quantity && this.count(current, newIdentity) === newCount + quantity) return;
      throw failure || new Error('Velikost se nepodařilo změnit. Zkuste to prosím znovu.');
    } catch (error) {
      current = await this.readCart();
      if (this.count(current, oldIdentity) === oldCount - quantity && this.count(current, newIdentity) === newCount + quantity) return;
      // If the original line is still present, undo only the newly added quantity.
      if (this.count(current, oldIdentity) === oldCount) {
        let excess = this.count(current, newIdentity) - newCount;
        for (const line of this.matching(current, newIdentity)) {
          if (excess <= 0) break;
          const removed = Math.min(excess, line.quantity);
          await this.request('cart/change.js', { id: line.key, quantity: line.quantity - removed });
          excess -= removed;
        }
      }
      throw error;
    }
  }
}

class OzeraCartVariant extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.select.disabled = false;
    this.abort = new AbortController();
    this.addEventListener('change', event => {
      // The theme's cart listener treats every bubbling change as a quantity edit.
      event.stopPropagation();
      this.changeVariant();
    }, { signal: this.abort.signal });
  }
  disconnectedCallback() { this.abort?.abort(); }

  async request(path, body) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || 'Košík se nepodařilo aktualizovat. Zkuste to prosím znovu.');
    return data;
  }

  async refresh(variantId, message, isError) {
    const pageSections = ['main-cart-items', 'main-cart-footer'].map(id => document.getElementById(id)).filter(Boolean);
    const sections = ['cart-drawer', 'cart-icon-bubble', ...pageSections.map(element => element.dataset.id)];
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=${sections.map(encodeURIComponent).join(',')}&sections_url=${encodeURIComponent(window.location.pathname)}`);
    if (!response.ok) throw new Error();
    const html = await response.json();
    if (sections.some(id => !html[id])) throw new Error();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.renderContents({ sections: html }, true);
    for (const element of pageSections) {
      const parsed = new DOMParser().parseFromString(html[element.dataset.id], 'text/html');
      const target = element.querySelector('.js-contents') || element;
      target.innerHTML = parsed.querySelector('.js-contents').innerHTML;
    }
    if (!drawer) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = new DOMParser().parseFromString(html['cart-icon-bubble'], 'text/html').querySelector('.shopify-section').innerHTML;
    }
    const scope = this.inDrawer ? drawer : document.querySelector('cart-items');
    const picker = [...scope.querySelectorAll('ozera-cart-variant')].find(element => Number(element.dataset.variantId) === Number(variantId)) || scope.querySelector('ozera-cart-variant');
    if (picker) {
      const status = picker.querySelector('[data-variant-status]');
      status.textContent = message;
      status.classList.toggle('is-error', isError);
      if (this.inDrawer && drawer.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), picker.select);
      else picker.select.focus({ preventScroll: true });
    }
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
  }

  async changeVariant() {
    const original = Number(this.dataset.variantId);
    const selected = Number(this.select.value);
    if (selected === original) return;
    if (OzeraCartVariant.pending || document.querySelector('ozera-cart-protection[aria-busy="true"]')) {
      this.select.value = original;
      return;
    }
    OzeraCartVariant.pending = true;
    this.inDrawer = Boolean(this.closest('cart-drawer'));
    const selector = 'cart-items, cart-drawer, #main-cart-footer';
    const controls = [...document.querySelectorAll(`${selector.split(', ').map(scope => `${scope} button, ${scope} input, ${scope} select`).join(', ')}`)]
      .map(element => ({ element, disabled: element.disabled }));
    controls.forEach(({ element }) => { element.disabled = true; });
    const scopes = [...document.querySelectorAll(selector)];
    scopes.forEach(element => element.setAttribute('aria-busy', 'true'));
    const status = this.querySelector('[data-variant-status]');
    status.textContent = 'Měním velikost…';
    let message = 'Velikost byla změněna.';
    let isError = false;
    try { await new OzeraCartVariantChange(this.request.bind(this)).replace(this.dataset.lineKey, selected); }
    catch (error) { message = error.message; isError = true; }
    try { await this.refresh(isError ? original : selected, message, isError); }
    catch {
      // Reconcile unknown network outcomes by reloading the authoritative cart.
      window.location.reload();
      return;
    } finally {
      controls.forEach(({ element, disabled }) => { element.disabled = disabled; });
      scopes.forEach(element => element.removeAttribute('aria-busy'));
      OzeraCartVariant.pending = false;
    }
  }
}
if (!customElements.get('ozera-cart-variant')) customElements.define('ozera-cart-variant', OzeraCartVariant);

// Cart-page remove controls are links, so disabled alone cannot lock them.
document.addEventListener('click', event => {
  if (OzeraCartVariant.pending && event.target.closest('cart-remove-button, a[href*="/cart/change"]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
